import { zodResolver } from "@hookform/resolvers/zod";
import { APP_CLINIC } from "core/consts";
import { Env } from "core/types";
import {
  Button,
  ControlledInput,
  ControlledPasswordInput,
  Spinner,
} from "ds/ui";
import {
  LoginErrorState,
  LoginView,
} from "dsl/ecosystems/CareproviderOnboardingLoginPage/useHandleLogin";
import { LoginPageVariants } from "dsl/ecosystems/LoginPageV2/classes";
import { LoginPageLayout } from "dsl/ecosystems/LoginPageV2/LoginPageLayout";
import { useLoginFieldSideEffects } from "dsl/ecosystems/LoginPageV2/useLoginFieldSideEffects";
import { LockIcon } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { getSenderLoginDefaultValues } from "./getDefaultValues";
import {
  getSenderLoginSchema,
  SenderLoginFormValues,
  SenderLoginParsedValues,
  SenderLoginSubmitPayload,
} from "./schema";

export type SenderLoginFormProps = LoginPageVariants & {
  challengeTimestamp: string | undefined;
  contactUsUrl?: string;
  env: Env;
  goToForgotPassword: string;
  goToProductionUrl: string;
  isEmailInputLoading?: boolean;
  isLoading: boolean;
  isSingleSignOn?: boolean;
  loginErrors?: LoginErrorState;
  loginView: LoginView;
  onBackToLogin: () => void;
  onChangeEmail?: (value: string) => void;
  onSubmit: (payload: SenderLoginSubmitPayload) => void;
  otherAppUrl?: string;
  resetError: () => void;
  ssoToken?: string;
};

type ResolverCtx = {
  isSingleSignOn: boolean;
  translations: Translations;
  view: LoginView;
};

export function SenderLoginForm({
  app = APP_CLINIC,
  challengeTimestamp,
  contactUsUrl,
  env,
  goToForgotPassword,
  goToProductionUrl,
  isEmailInputLoading,
  isLoading,
  isSingleSignOn = false,
  loginErrors,
  loginView = "login",
  onBackToLogin,
  onChangeEmail,
  onSubmit,
  otherAppUrl,
  resetError,
  ssoToken,
}: SenderLoginFormProps) {
  const translations = useTranslations();

  const isLogin = loginView === "login";

  const resolverCtxRef = useRef<ResolverCtx>({
    isSingleSignOn,
    translations,
    view: loginView,
  });
  resolverCtxRef.current = {
    isSingleSignOn,
    translations,
    view: loginView,
  };

  const methods = useForm<SenderLoginFormValues>({
    defaultValues: getSenderLoginDefaultValues({ ssoToken }),
    resolver: async (values, context, options) => {
      const schema = getSenderLoginSchema(resolverCtxRef.current);
      return zodResolver(schema)(values, context, options);
    },
  });

  useEffect(() => {
    methods.setValue("ssoToken", ssoToken, { shouldDirty: false });
  }, [methods, ssoToken]);

  useLoginFieldSideEffects({
    onChangeEmail,
    resetError,
    watch: methods.watch,
  });

  useEffect(() => {
    (["email", "password", "challenge"] as const).forEach((field) => {
      const externalMessage = loginErrors?.[field];
      if (externalMessage) {
        methods.setError(field, { message: externalMessage });
      } else if (methods.getFieldState(field).error?.type === "manual") {
        methods.clearErrors(field);
      }
    });
  }, [loginErrors, methods]);

  const handleValid = useCallback(
    (values: SenderLoginParsedValues) => {
      onSubmit(values);
    },
    [onSubmit],
  );

  const handleBackToLogin = useCallback(() => {
    methods.reset(getSenderLoginDefaultValues({ ssoToken }));
    onBackToLogin();
  }, [methods, onBackToLogin, ssoToken]);

  const submitButtonId = isLogin
    ? isSingleSignOn
      ? "login_sso"
      : "login"
    : "login_challenge";

  return (
    <FormProvider {...methods}>
      <LoginPageLayout
        app={app}
        autoFocus="back-button"
        challengeField={
          <ControlledInput
            autoComplete="off"
            label={translations.login.loginPage.verificationCode}
            name="challenge"
            placeholder={
              translations.login.loginPage.verificationCodePlaceholder
            }
            required
            type={isLogin ? "hidden" : "text"}
          />
        }
        challengeTimestamp={challengeTimestamp}
        contactUsUrl={contactUsUrl}
        emailField={
          <ControlledInput
            autoComplete="email"
            endContent={
              isEmailInputLoading ? (
                <Spinner size="sm" testId="email-input-spinner" />
              ) : undefined
            }
            label={translations.login.loginPage.email}
            name="email"
            placeholder={translations.login.loginPage.emailPlaceholder}
            required
            type={isLogin ? "email" : "hidden"}
          />
        }
        env={env}
        goToForgotPassword={goToForgotPassword}
        goToProductionUrl={goToProductionUrl}
        isLoading={isLoading}
        isSingleSignOn={isSingleSignOn}
        loginErrors={loginErrors}
        loginView={loginView}
        otherAppUrl={otherAppUrl}
        passwordField={
          <ControlledPasswordInput
            autoComplete={
              isLogin && !isSingleSignOn ? "current-password" : "off"
            }
            label={translations.login.loginPage.password}
            name="password"
            placeholder={translations.login.loginPage.passwordPlaceholder}
            required
            type={isLogin && !isSingleSignOn ? "password" : "hidden"}
          />
        }
        ssoInfo={
          isSingleSignOn && (
            <div
              aria-live="polite"
              className="flex max-w-fit items-center space-x-2 rounded-md border border-success bg-success-light py-1.5 pr-5 pl-3 text-success-dark"
              role="alert"
            >
              <LockIcon aria-hidden="true" size={14} />{" "}
              <p className="text-sm">{translations.login.loginPage.ssoInfo}</p>
            </div>
          )
        }
        submit={
          <Button
            color="primary"
            fullWidth
            id={submitButtonId}
            isLoading={isLoading}
            size="lg"
            type="submit"
            variant="solid"
          >
            {translations.login.loginPage.loginButton}
          </Button>
        }
        onBackToLogin={handleBackToLogin}
        onSubmit={methods.handleSubmit(handleValid)}
      />
    </FormProvider>
  );
}
